// Right zone: context tabs Agent / Mission / Map / Contacts / Intel / Log
// (prototype renderCtx, agentPanelHTML, missionHTML, mapSVG, contactsHTML, intelHTML). The Agent tab shows with
// features.agent (Phase 3, Agent.tsx).
// Phase 5 (features.deterrence): mission steps bound to talk_down / relay open their drawers; the Map shows the site's
// speakers and relays with live state (click one to use it); Contacts lists what was sent to the customer; the Log
// plays a talk-down's stored audio.

import { useMemo, useState } from 'react';
import {
  api,
  errorText,
  CALL_OUTCOMES,
  callIsLive,
  callResult,
  type CallOutcome,
  type CallResult,
  type CallStep,
  type EventAction,
  type MissionStep,
  type WatchSummaryItem,
} from '../api.ts';
import { Icon, type IconName } from '../components/Icon.tsx';
import { SitemapView } from '../components/SitemapView.tsx';
import { href } from '../router.ts';
import { useSession } from '../session.tsx';
import { expectCall, softphoneFor, useSoftphoneState, type PhoneCall, type Softphone } from '../telephony/index.ts';
import { CallControls } from '../telephony/CallPanel.tsx';
import { fmtDateTime, fmtTime, tzAbbr } from '../time.ts';
import { AgentTab, PainChips } from './Agent.tsx';
import { CallStateLine, RecordingView } from './Calls.tsx';
import type { Ctl, CtxTab } from './ctl.ts';
import { EventAudit } from './EventAudit.tsx';
import { AudioButton, liveMarks, NotificationList } from './DeterDrawers.tsx';
import { deviceActive, functionIcon } from './devices.ts';
import { useNow } from '../hooks.ts';
import { actorCls, actorName, addressLine, camShort, dur, fmtPhone, human, ms, str } from './util.ts';

const TAB_META: Record<CtxTab, { label: string; icon: IconName }> = {
  agent: { label: 'Agent', icon: 'spark' },
  mission: { label: 'Mission', icon: 'list' },
  map: { label: 'Map', icon: 'map' },
  contacts: { label: 'Contacts', icon: 'phone' },
  intel: { label: 'Intel', icon: 'chart' },
  log: { label: 'Log', icon: 'log' },
};

/* ---------- mission ---------- */

function stepButton(c: Ctl, s: MissionStep): { label: string; run: () => void; needsAct: boolean } {
  switch (s.binding) {
    case 'video_review':
      return { label: 'Sync', run: () => c.toggleSync(true), needsAct: false };
    case 'call':
      return { label: 'Call list', run: () => c.setCtx('contacts'), needsAct: false };
    case 'dispatch':
      return { label: 'Dispatch', run: () => c.openDrawer({ k: 'dispatch' }), needsAct: true };
    case 'note':
      return { label: 'Note', run: () => c.openDrawer({ k: 'note' }), needsAct: true };
    case 'talk_down':
      if (c.features.deterrence) return { label: 'Talk', run: () => c.openDrawer({ k: 'talkdown' }), needsAct: true };
      return { label: 'Check', run: () => c.setStep(s, 'done'), needsAct: true };
    case 'relay':
      if (c.features.deterrence) return { label: 'Deterrents', run: () => c.openDrawer({ k: 'deter' }), needsAct: true };
      return { label: 'Check', run: () => c.setStep(s, 'done'), needsAct: true };
    default:
      // manual, and talk_down / relay while deterrence is off: checked by hand
      return { label: 'Check', run: () => c.setStep(s, 'done'), needsAct: true };
  }
}

function Mission({ c }: { c: Ctl }) {
  const ev = c.ev;
  const site = ev.site;
  const m = ev.mission;
  const steps = m ? m.steps.slice().sort((a, b) => a.seq - b.seq) : [];
  const nextId = steps.find((s) => s.status === 'pending')?.id;
  return (
    <>
      {(site.specialInstructions || site.authorizedActivity.length > 0) && (
        <div className="special">
          <span className="lbl">
            <Icon name="alert" /> Special instructions
          </span>
          {site.specialInstructions ? <span className="pre">{site.specialInstructions}</span> : <span className="muted">None on file.</span>}
          {site.authorizedActivity.length > 0 && (
            <div className="authz">
              <span className="lbl">Authorized activity</span>
              {site.authorizedActivity.map((a, i) => (
                <span key={i}>
                  <b>{a.label}</b>
                  {a.days?.length ? ` · ${a.days.join(', ')}` : ''}
                  {a.start && a.end ? ` · ${a.start}–${a.end}` : ''}
                  {a.note ? ` · ${a.note}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="sec">
        <div className="lbl">
          <span>Mission · {m ? m.name : 'none'}</span>
          {m && <span className="dim">v{m.version}</span>}
        </div>
        {!m ? (
          <div className="note">No mission is assigned for this event type. Follow the special instructions and close with a disposition.</div>
        ) : (
          <div className="steps">
            {steps.map((s, i) => {
              const done = s.status !== 'pending';
              const b = stepButton(c, s);
              return (
                <div key={s.id} className={`step${s.status === 'done' ? ' done' : ''}${s.status === 'na' ? ' na' : ''}${s.id === nextId ? ' next' : ''}`}>
                  <span className="ck" aria-label={s.status === 'done' ? 'Done' : s.status === 'na' ? 'Not applicable' : `Step ${i + 1}`}>
                    {s.status === 'done' ? <Icon name="check" /> : s.status === 'na' ? '–' : i + 1}
                  </span>
                  <div>
                    <div className="stx">
                      {s.title}
                      {s.required && <span className="req" title="Required before closing">required</span>}
                    </div>
                    {s.hint && <div className="hint">{s.hint}</div>}
                    {done && (
                      <div className={`by ${actorCls(s.doneBy)}`}>
                        {s.doneBy?.type === 'agent' && (
                          <span className="byai" title="The AI agent checked this step off (approved by a person while one holds the event)">
                            <Icon name="spark" />
                            by AI
                          </span>
                        )}
                        {s.status === 'na' ? 'Marked N/A' : 'Done'}
                        {s.doneBy?.type === 'agent' ? '' : ` by ${actorName(s.doneBy)}`}
                        {s.doneAt ? ` · ${fmtTime(s.doneAt, ev.siteTimeZone)}` : ''}
                        {s.note ? ` · ${s.note}` : ''}
                      </div>
                    )}
                  </div>
                  <div className="stepbtns">
                    {!done && (
                      <>
                        <button type="button" className={`btn${s.id === nextId ? ' pri' : ''}`} onClick={b.run} disabled={b.needsAct && !c.canAct}>
                          {b.label}
                        </button>
                        <button type="button" className="btn ghost" onClick={() => c.setStep(s, 'na')} disabled={!c.canAct} title="Not applicable to this event">
                          N/A
                        </button>
                      </>
                    )}
                    {done && s.binding === 'manual' && c.canAct && (
                      <button type="button" className="btn ghost" onClick={() => c.setStep(s, 'pending')} title="Uncheck">
                        Undo
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="note">Steps check off on their own when you take the action. Required steps must be done or N/A before you can close.</div>
    </>
  );
}

/* ---------- map ---------- */

function MapTab({ c }: { c: Ctl }) {
  const ev = c.ev;
  const maps = ev.sitemaps.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const withSel = maps.find((m) => m.placements.some((p) => p.entity_type === 'camera' && p.entity_id === c.view.cameraId));
  const [pick, setPick] = useState<string | null>(null);
  const map = maps.find((m) => m.id === pick) ?? withSel ?? maps[0];
  const alarmCams = new Set(ev.alarms.map((a) => a.cameraId).filter((x): x is string => !!x));
  const deter = c.features.deterrence;
  const devs = [...c.devices.speakers, ...c.devices.relays];
  const anyActive = devs.some((d) => deviceActive(d));
  const now = useNow(anyActive ? 500 : 5000);
  const marks = useMemo(() => (deter ? liveMarks(c, now) : undefined), [deter, c, now]);
  const sel = useMemo(() => new Set(c.speakers), [c.speakers]);
  const pickDevice = (id: string) => {
    const d = c.devices.byId.get(id);
    if (!d) return;
    if (d.kind === 'speaker') {
      c.setSpeakers([id]);
      c.openDrawer({ k: 'talkdown' });
    } else c.openDrawer({ k: 'deter', relayId: id });
  };
  return (
    <>
      <div className="sec">
        <div className="lbl">
          <span>Site map</span>
          {map && (
            <span className="src">
              <Icon name="lock" />
              ERP · {map.name}
              {map.revisionLabel ? ` · ${map.revisionLabel}` : ''}
            </span>
          )}
        </div>
        {maps.length > 1 && (
          <div className="seg wrap" role="radiogroup" aria-label="Site map">
            {maps.map((m) => (
              <button key={m.id} type="button" role="radio" aria-checked={m.id === map?.id} className={m.id === map?.id ? 'on' : ''} onClick={() => setPick(m.id)}>
                {m.name}
              </button>
            ))}
          </div>
        )}
        {map ? (
          <>
            <div className="mapwrap">
              <SitemapView map={map} cameras={ev.cameras} devices={[]} alarmCameraIds={alarmCams} selectedCameraId={c.view.cameraId} onCameraClick={c.selectCamera} compact live={marks} selectedDeviceIds={deter ? sel : undefined} onDeviceClick={deter && c.canAct ? pickDevice : undefined} />
            </div>
            <div className="mapkey">
              <span>
                <i style={{ borderColor: 'var(--accent)', borderRadius: '50%' }} />
                Camera
              </span>
              <span>
                <i style={{ borderColor: 'var(--crit)', borderRadius: '50%' }} />
                Alarm camera
              </span>
              <span>
                <i style={{ borderColor: 'var(--accent)', background: 'var(--accent)', borderRadius: '50%' }} />
                In player
              </span>
              {deter && (
                <>
                  <span>
                    <i style={{ borderColor: 'var(--low)', borderRadius: '3px' }} />
                    Speaker
                  </span>
                  <span>
                    <i style={{ borderColor: 'var(--med)', transform: 'rotate(45deg)', borderRadius: '1px' }} />
                    Relay
                  </span>
                  <span>
                    <i style={{ borderColor: 'var(--med)', background: 'var(--med)', transform: 'rotate(45deg)', borderRadius: '1px' }} />
                    On / playing
                  </span>
                </>
              )}
            </div>
            <div className="note">Click a camera to view it.{deter ? (c.canAct ? ' Click a speaker to talk on it, a relay to open the deterrents.' : '') : ''} Placements come from the ERP site map.</div>
          </>
        ) : (
          <div className="note">No site map for this site.</div>
        )}
      </div>
      {deter && devs.length > 0 && (
        <div className="sec">
          <div className="lbl">
            <span>Speakers and relays</span>
            <span className="dim">live</span>
          </div>
          <div className="devlist">
            {devs.map((d) => {
              const on = deviceActive(d, now);
              const left = on && d.until ? Math.max(0, (Date.parse(d.until) || 0) - now) : null;
              return (
                <button key={d.id} type="button" className={`devrow k-${d.kind}${on ? ' on' : ''}${sel.has(d.id) ? ' sel' : ''}`} onClick={() => pickDevice(d.id)} disabled={!c.canAct} title={c.canAct ? (d.kind === 'speaker' ? 'Talk on this speaker' : 'Open the deterrents') : 'Read-only'}>
                  <Icon name={d.kind === 'speaker' ? 'speaker' : functionIcon(d.function)} />
                  <span className="dn">{d.kind === 'speaker' ? (d.zone ?? d.name) : d.name}</span>
                  <span className={`devst${on ? ' on' : ''}`}>{on ? (d.kind === 'speaker' ? 'playing' : left !== null ? `ON ${dur(left)}` : 'HOLDING') : d.kind === 'speaker' ? 'idle' : 'off'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="sec">
        <div className="lbl">Address</div>
        <div>
          {addressLine(ev) || <span className="muted">No address on file</span>}
          {ev.site.crossStreets && (
            <>
              <br />
              <span className="muted">{ev.site.crossStreets}</span>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- contacts ---------- */

/** This row's calls, oldest first. */
function callsFor(actions: EventAction[], s: CallStep): EventAction[] {
  return actions
    .filter((a) => a.type === 'call' && a.params.contactId === s.contactId && a.params.phoneId === s.phoneId)
    .sort((a, b) => (ms(a.startedAt) ?? 0) - (ms(b.startedAt) ?? 0));
}

function outcomeLabel(a: EventAction): string | null {
  const o = str(a.result?.outcome);
  if (!o) return a.status === 'cancelled' ? 'Cancelled' : null;
  return CALL_OUTCOMES.find((x) => x.value === o)?.label ?? o.replace(/_/g, ' ');
}

/**
 * The phone's own call for this row's live call: by its telephony session, or (before the server has linked the
 * session) the phone's only live outbound call. Its controls show on the row; the phone panel stays shut.
 */
function phoneCallFor(r: CallResult, calls: readonly PhoneCall[]): PhoneCall | null {
  const live = calls.filter((x) => x.state !== 'ended' && !x.consultFor);
  if (r.telephonySessionId) return live.find((x) => x.telephonySessionId === r.telephonySessionId) ?? null;
  const out = live.filter((x) => x.direction === 'outbound');
  return out.length === 1 ? out[0]! : null;
}

/** A call on this event is dialing, ringing or connected. */
export function hasLiveCall(actions: EventAction[]): boolean {
  return actions.some((a) => a.type === 'call' && callIsLive(callResult(a)));
}

function conditionText(s: CallStep): string | null {
  const cnd = s.conditions;
  if (!cnd) return null;
  const parts: string[] = [];
  if (cnd.analytics?.length) parts.push(`only for ${cnd.analytics.map((x) => human(x)).join(', ')}`);
  if (cnd.window) parts.push(`${cnd.window.start}–${cnd.window.end} site time`);
  return parts.length ? parts.join(' · ') : null;
}

/** Sign-in / connection state of the browser softphone, shown above the call list. */
function SoftphoneBanner({ sp }: { sp: Softphone }) {
  const st = useSoftphoneState(sp);
  if (st.status === 'error') {
    return (
      <div className="banner warnish" role="alert">
        <Icon name="alert" />
        <span>
          <b>{sp.label} phone unavailable.</b> {st.error} Dial from a desk phone and record the outcome.
        </span>
      </div>
    );
  }
  if (st.loggedIn === false) {
    return (
      <div className="banner warnish" role="alert">
        <Icon name="phone" />
        <span>
          <b>Sign in to {sp.label}</b> to place calls from the console. You sign in in the phone panel; calls are recorded.
        </span>
        <button type="button" className="btn sm pri" onClick={() => sp.setOpen(true)}>
          Open phone
        </button>
      </div>
    );
  }
  if (st.status !== 'ready') {
    return (
      <div className="note">
        <span className="spinner" aria-hidden="true" /> Connecting the {sp.label.toLowerCase()}…
      </div>
    );
  }
  if (st.error) {
    return (
      <div className="banner warnish" role="status">
        <Icon name="alert" />
        <span>{st.error}</span>
      </div>
    );
  }
  return null;
}

function Contacts({ c }: { c: Ctl }) {
  const ev = c.ev;
  const auth = ev.authority;
  const config = useSession().config;
  const sp = softphoneFor(config);
  const spState = useSoftphoneState(sp);
  const anyLive = hasLiveCall(ev.actions);
  const phoneBlocked = !!sp && (spState.status === 'error' || spState.loggedIn === false);

  const start = async (s: CallStep) => {
    const r = await c.run(
      'Call',
      (v) => api.startCall(ev.id, { contactId: s.contactId, phoneId: s.phoneId }, v),
      (res) => res.event ?? null,
    );
    if (!r) return;
    if (!r.event) c.reload();
    if (r.mode === 'ringcentral') {
      const e164 = r.dial.e164 ?? s.e164;
      if (!sp || !e164) {
        c.toast({ tone: 'warn', title: 'RingCentral phone not available', body: `Dial ${fmtPhone(e164, r.dial.extension)} from a desk phone and record the outcome.` });
        return;
      }
      expectCall({ eventId: ev.id, actionId: r.action.id, e164 });
      sp.dial(e164, null, { openPanel: false }).catch((err: unknown) => c.toast({ tone: 'warn', title: 'The phone could not place the call', body: `${errorText(err)}. Dial ${fmtPhone(e164, r.dial.extension)} from a desk phone and record the outcome.` }));
    }
  };

  const hangup = (a: EventAction, r: CallResult) => {
    if (r.provider === 'ringcentral') {
      if (sp) sp.hangup(r.telephonySessionId).catch((err: unknown) => c.toast({ tone: 'warn', title: 'Hang up in RingCentral', body: errorText(err) }));
      else c.toast({ tone: 'warn', title: 'Hang up in RingCentral', body: 'The RingCentral phone is not on in this console.' });
      return;
    }
    void c.run('Hang up', (v) => api.callHangup(ev.id, a.id, v), (d) => d);
  };

  const setOutcome = (a: EventAction, outcome: CallOutcome) => void c.run('Call outcome', (v) => api.callOutcome(ev.id, a.id, { outcome }, v), (d) => d);

  const reportWrong = (s: CallStep) =>
    c.openDrawer({
      k: 'dataIssue',
      prefill: {
        entityType: 'contact',
        entityId: s.contactId,
        subItem: { kind: 'phone', id: s.phoneId },
        field: 'e164',
        issueType: 'phone_wrong_person',
        severity: 'degrades_response',
        description: `Wrong number for ${s.contactName} (${fmtPhone(s.e164, s.extension)}) found while handling ${ev.displayNo}.`,
      },
    });

  return (
    <>
      <div className="sec">
        <div className="lbl">
          <span>Call procedure · {human(ev.primaryAnalytic)}</span>
          <span className="src">
            <Icon name="lock" />
            Contacts from ERP
          </span>
        </div>
        {sp && c.canAct && <SoftphoneBanner sp={sp} />}
        {ev.callProcedure.length === 0 && <div className="note">No call procedure is set for this site. A supervisor can add one in Setup.</div>}
        {ev.callProcedure
          .slice()
          .sort((a, b) => a.seq - b.seq)
          .map((s, i) => {
            const calls = callsFor(ev.actions, s);
            const last = calls[calls.length - 1];
            const r = last ? callResult(last) : null;
            const live = !!r && callIsLive(r);
            const lastOut = last && !live ? outcomeLabel(last) : null;
            const needsOutcome = !!last && !!r && !r.outcome && last.status !== 'cancelled';
            const wasWrong = r?.outcome === 'wrong_number';
            const showBox = !!last && !!r && (live || needsOutcome || (r.recording !== null && r.provider !== 'manual'));
            const cond = conditionText(s);
            const manual = r?.provider === 'manual';
            const phoneCall = live && sp && r ? phoneCallFor(r, spState.phone.calls) : null;
            return (
              <div key={`${s.seq}-${s.contactId}-${s.phoneId}`} className={`contact${!s.applies ? ' na' : ''}${s.broken ? ' broken' : ''}${live ? ' live' : ''}`}>
                <span className="o">{i + 1}</span>
                <div className="cmain">
                  <div className="nm">
                    {s.contactName}
                    {s.broken && <span className="pill bad tiny-pill">broken</span>}
                    {!s.applies && !s.broken && <span className="pill tiny-pill">not now</span>}
                  </div>
                  <div className="muted small">
                    {s.jobTitle ?? 'Contact'}
                    {s.phoneType ? ` · ${s.phoneType}` : ''}
                    {cond ? ` · ${cond}` : ''}
                  </div>
                  <div className="ph">{fmtPhone(s.e164, s.extension)}</div>
                  {s.broken && <div className="note bad-t">Contact or phone is missing or inactive in the ERP replica.</div>}
                </div>
                <div className="ctail">
                  {live ? (
                    <span className="pill ok tiny-pill">
                      <i className="dot" aria-hidden="true" />
                      on call
                    </span>
                  ) : (
                    <>
                      {lastOut && <span className={`pill ${lastOut === 'Answered' ? 'ok' : wasWrong ? 'bad' : ''}`}>{lastOut}</span>}
                      <button
                        type="button"
                        className={`btn${s.applies && !last && i === ev.callProcedure.findIndex((x) => x.applies && !x.broken) ? ' pri' : ''}`}
                        onClick={() => void start(s)}
                        disabled={!c.canAct || s.broken || !s.e164 || anyLive || !!c.busy || phoneBlocked}
                        title={
                          s.broken
                            ? 'Broken contact: report it to the ERP owner'
                            : anyLive
                              ? 'Finish the call in progress first'
                              : phoneBlocked
                                ? `Sign in to ${sp?.label ?? 'the phone'} first`
                                : undefined
                        }
                      >
                        <Icon name="phone" />
                        {last ? 'Again' : 'Call'}
                      </button>
                    </>
                  )}
                </div>
                {showBox && last && r && (
                  <div className={`dial${live ? '' : ' after'}`}>
                    {manual && live ? (
                      <>
                        <span className="lbl">Dial manually</span>
                        <span className="bignum mono">{fmtPhone(s.e164)}</span>
                        {s.extension && <span className="mono">ext. {s.extension}</span>}
                      </>
                    ) : (
                      <div className="crow">
                        <CallStateLine a={last} r={r} />
                        <span className="sp" />
                        {r.provider === 'simulator' && <span className="pill tiny-pill">simulated</span>}
                        {live && c.canAct && (
                          <button type="button" className="btn sm badb" onClick={() => hangup(last, r)} disabled={!!c.busy}>
                            <Icon name="x" />
                            Hang up
                          </button>
                        )}
                      </div>
                    )}
                    {phoneCall && sp && c.canAct && <CallControls sp={sp} call={phoneCall} all={spState.phone.calls} compact />}
                    {needsOutcome && c.canAct && (
                      <>
                        <span className="lbl">{live ? 'Outcome (you can set it now or after)' : 'Outcome'}</span>
                        <div className="outs" role="group" aria-label="Call outcome">
                          {CALL_OUTCOMES.map((o) => (
                            <button key={o.value} type="button" className={`btn${o.value === 'answered' ? ' okb' : ''}`} disabled={!!c.busy} onClick={() => setOutcome(last, o.value)}>
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                    {r.outcome && <span className="small muted">Outcome: {outcomeLabel(last)}</span>}
                    {r.recording && r.provider !== 'manual' && <RecordingView eventId={ev.id} actionId={last.id} rec={r.recording} />}
                    {(live || needsOutcome) && c.canAct && (
                      <button type="button" className="btn ghost sm" onClick={() => reportWrong(s)}>
                        <Icon name="alert" />
                        Report wrong number
                      </button>
                    )}
                  </div>
                )}
                {!showBox && wasWrong && (
                  <div className="outs">
                    <button type="button" className="btn ghost sm" onClick={() => reportWrong(s)} disabled={!c.canAct}>
                      <Icon name="alert" />
                      Report wrong number
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        <div className="note">Order and rules are set in the SOC. Names and numbers come from the ERP, so a wrong number is fixed there: report it and the ERP owner gets a work item.</div>
      </div>
      {c.features.deterrence && (
        <div className="sec">
          <div className="lbl">
            <span>Customer notifications</span>
            <button type="button" className="btn sm" onClick={() => c.openDrawer({ k: 'notify' })} disabled={!c.canAct}>
              <Icon name="send" />
              Notify customer
            </button>
          </div>
          <NotificationList c={c} compact />
        </div>
      )}
      <div className="sec">
        <div className="lbl">
          <span>Authority</span>
          <span className="dim">{auth ? auth.kind : 'none matched'}</span>
        </div>
        {auth ? (
          <div className="contact">
            <span className="o">
              <Icon name="shield" />
            </span>
            <div className="cmain">
              <div className="nm">{auth.name}</div>
              <div className="muted small">Non-emergency{auth.note ? ` · ${auth.note}` : ''}</div>
              <div className="ph big">{fmtPhone(auth.nonEmergency)}</div>
            </div>
            <div className="ctail">
              <button type="button" className="btn" onClick={() => c.openDrawer({ k: 'dispatch' })} disabled={!c.canAct}>
                <Icon name="shield" />
                Dispatch
              </button>
            </div>
          </div>
        ) : (
          <div className="note">No authority is assigned or matched by geo. Use the local non-emergency line and record it in the dispatch form.</div>
        )}
        <div className="contact e911">
          <span className="o">
            <Icon name="alert" />
          </span>
          <div className="cmain">
            <div className="nm">{auth?.emergency ? fmtPhone(auth.emergency) : '911'} · Life safety only</div>
            <div className="muted small">Fire, medical, or a person in danger. Not for property crime.</div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------- intel ---------- */

function Gauge({ value }: { value: number }) {
  const R = Math.max(0, Math.min(100, value));
  const ang = (R / 100) * 270;
  const rad = (d: number) => ((d - 225) * Math.PI) / 180;
  const P = (d: number, r = 26) => `${(32 + r * Math.cos(rad(d))).toFixed(1)} ${(32 + r * Math.sin(rad(d))).toFixed(1)}`;
  const col = R >= 75 ? 'var(--crit)' : R >= 55 ? 'var(--high)' : 'var(--med)';
  return (
    <svg className="gauge" viewBox="0 0 64 64" role="img" aria-label={`Site risk ${R} of 100`}>
      <path d={`M${P(0)} A26 26 0 1 1 ${P(270)}`} fill="none" stroke="var(--line2)" strokeWidth="6" strokeLinecap="round" />
      {R > 0 && <path d={`M${P(0)} A26 26 0 ${ang > 180 ? 1 : 0} 1 ${P(ang)}`} fill="none" stroke={col} strokeWidth="6" strokeLinecap="round" />}
      <text x="32" y="36" textAnchor="middle" fontFamily="var(--f-mono)" fontSize="15" fill="var(--fg)">
        {R}
      </text>
    </svg>
  );
}

/** Video watch time on this event per camera (EventDetail.watch_summary). */
function VideoReviewed({ items }: { items: WatchSummaryItem[] | undefined }) {
  if (!items) return null;
  const rows = items.slice().sort((a, b) => b.liveMs + b.archiveMs - (a.liveMs + a.archiveMs));
  const total = rows.reduce((t, r) => t + r.liveMs + r.archiveMs, 0);
  return (
    <div className="sec">
      <div className="lbl">
        <span>Video reviewed</span>
        <span className="dim">{rows.length ? `${dur(total)} on ${rows.length} camera${rows.length === 1 ? '' : 's'}` : ''}</span>
      </div>
      {rows.length === 0 ? (
        <div className="note">No video watched on this event yet.</div>
      ) : (
        <div className="hist watch">
          <div className="whead">
            <span>Camera</span>
            <span>Live</span>
            <span>Archive</span>
          </div>
          {rows.map((r) => (
            <div key={r.cameraId} title={`${r.views} view${r.views === 1 ? '' : 's'}`}>
              <span className="wcam" title={r.cameraName ?? r.cameraId}>
                {camShort(r.cameraName ?? r.cameraId)}
                <span className="dim"> ×{r.views}</span>
              </span>
              <span className={`mono${r.liveMs ? '' : ' dim'}`}>{dur(r.liveMs)}</span>
              <span className={`mono${r.archiveMs ? '' : ' dim'}`}>{dur(r.archiveMs)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Intel({ c }: { c: Ctl }) {
  const ev = c.ev;
  const site = ev.site;
  const tz = ev.siteTimeZone;
  const R = site.riskLevel;
  const weekly = ev.history.weekly.slice(-12);
  const max = Math.max(1, ...weekly);
  const total = weekly.reduce((a, b) => a + b, 0);
  const col = R === null ? 'var(--muted)' : R >= 75 ? 'var(--crit)' : R >= 55 ? 'var(--high)' : 'var(--med)';
  const links = ev.links;
  return (
    <>
      <div className="risk">
        {R !== null ? <Gauge value={R} /> : <div className="gauge none">—</div>}
        <div>
          <div className="lbl">Site risk</div>
          <div className="strong" style={{ color: col }}>
            {R === null ? 'Not set' : R >= 75 ? 'High' : R >= 55 ? 'Elevated' : 'Moderate'}
          </div>
          <div className="note">{site.riskReason ?? 'No reason recorded.'}</div>
        </div>
      </div>
      <div className="sec">
        <div className="lbl">Pain points</div>
        {site.painPoints.length ? (
          <>
            <PainChips p={c.agent.data?.visible ? c.agent.data : null} all={site.painPoints} />
            {c.agent.data?.visible && (c.agent.data.assessment?.painPoints.length ?? 0) > 0 && <div className="note">Violet = matched by the AI on this event.</div>}
          </>
        ) : (
          <div className="note">None recorded.</div>
        )}
      </div>
      <VideoReviewed items={ev.watch_summary} />
      <div className="sec">
        <div className="lbl">
          <span>Events · last 12 weeks</span>
          <span className="dim">{total} total</span>
        </div>
        <svg viewBox="0 0 292 60" className="bars" role="img" aria-label={`Events per week, oldest to newest: ${weekly.join(', ')}`}>
          {weekly.map((v, i) => (
            <g key={i}>
              <rect x={i * 24 + 4} y={56 - (v / max) * 48} width="16" height={Math.max(1, (v / max) * 48)} rx="2" fill={i === weekly.length - 1 ? 'var(--accent)' : 'rgba(76,201,240,.35)'} />
              {v > 0 && (
                <text x={i * 24 + 12} y={52 - (v / max) * 48} textAnchor="middle" fontSize="8" fill="var(--muted)" fontFamily="var(--f-mono)">
                  {v}
                </text>
              )}
            </g>
          ))}
          <line x1="0" x2="292" y1="56.5" y2="56.5" stroke="var(--line2)" />
        </svg>
      </div>
      <div className="sec">
        <div className="lbl">Recent closed events</div>
        {ev.history.recent.length === 0 ? (
          <div className="note">No closed events at this site yet.</div>
        ) : (
          <div className="hist">
            {ev.history.recent.map((r) => (
              <div key={r.id}>
                <span className="mono dim">{fmtDateTime(r.closedAt, tz, false).slice(0, 6)}</span>
                <span>
                  <a href={href.console(r.id)} className="mono">
                    {r.displayNo}
                  </a>{' '}
                  {r.label ?? r.code ?? 'No disposition'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {(links.follows || links.splitFrom || links.mergedInto || links.splits.length > 0) && (
        <div className="sec">
          <div className="lbl">Linked events</div>
          <div className="hist">
            {links.follows && (
              <div>
                <span className="dim">Follows</span>
                <span>
                  <a href={href.console(links.follows.id)} className="mono">
                    {links.follows.displayNo}
                  </a>{' '}
                  · {links.follows.state}
                  {links.follows.closedAt ? ` · closed ${fmtTime(links.follows.closedAt, tz)} ${tzAbbr(tz)}` : ''}
                  {links.follows.disposition ? ` · ${links.follows.disposition}` : ''}
                </span>
              </div>
            )}
            {links.splitFrom && (
              <div>
                <span className="dim">Split from</span>
                <a href={href.console(links.splitFrom.id)} className="mono">
                  {links.splitFrom.displayNo}
                </a>
              </div>
            )}
            {links.mergedInto && (
              <div>
                <span className="dim">Merged into</span>
                <a href={href.console(links.mergedInto.id)} className="mono">
                  {links.mergedInto.displayNo}
                </a>
              </div>
            )}
            {links.splits.map((l) => (
              <div key={l.id}>
                <span className="dim">Split off</span>
                <span>
                  <a href={href.console(l.id)} className="mono">
                    {l.displayNo}
                  </a>{' '}
                  · {l.state}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="sec">
        <div className="lbl">Service</div>
        <div className="note">
          {site.servicePlan?.name ?? 'No service plan'}
          {site.vertical ? ` · ${site.vertical}` : ''}
          {site.inContractedHoursNow === false ? ' · outside contracted hours now' : ''}
        </div>
      </div>
    </>
  );
}

/* ---------- log ---------- */

const LOG_VIEW_KEY = 'eyeqsoc.logView';
function loadLogView(): 'log' | 'audit' {
  try {
    return localStorage.getItem(LOG_VIEW_KEY) === 'audit' ? 'audit' : 'log';
  } catch {
    return 'log';
  }
}

function Log({ c }: { c: Ctl }) {
  const ev = c.ev;
  const [view, setViewState] = useState<'log' | 'audit'>(loadLogView);
  const setView = (v: 'log' | 'audit') => {
    setViewState(v);
    try {
      localStorage.setItem(LOG_VIEW_KEY, v);
    } catch {
      /* ignore */
    }
  };
  const rows = ev.log.slice().sort((a, b) => b.seq - a.seq);
  // A stored recording plays under one log line per call: the "recording stored" line, else the newest line for that call.
  const recAt = new Map<number, EventAction>();
  for (const a of ev.actions) {
    if (a.type !== 'call' || callResult(a).recording?.status !== 'stored') continue;
    const refs = rows.filter((l) => l.refs.actionId === a.id);
    const target = refs.find((l) => l.kind.includes('recording_stored')) ?? refs[0];
    if (target) recAt.set(target.seq, a);
  }
  const toggle = (
    <div className="seg" role="radiogroup" aria-label="Log or audit">
      {(['log', 'audit'] as const).map((k) => (
        <button key={k} type="button" role="radio" aria-checked={view === k} className={view === k ? 'on' : ''} onClick={() => setView(k)}>
          {k === 'log' ? 'Log' : 'Audit'}
        </button>
      ))}
    </div>
  );
  if (view === 'audit') {
    return (
      <>
        {toggle}
        <EventAudit eventId={ev.id} tz={ev.siteTimeZone} />
      </>
    );
  }
  return (
    <>
      {toggle}
      <div className="sec">
        <div className="lbl">
          <span>Event log</span>
          <span className="dim">auto-recorded · append-only</span>
        </div>
        {rows.length === 0 ? (
          <div className="note">Nothing logged yet.</div>
        ) : (
          <div className="log">
            {rows.map((l) => {
              const call = recAt.get(l.seq);
              const rec = call ? callResult(call).recording : null;
              return (
                <div key={l.seq} className="le">
                  <span className="mono dim" title={fmtDateTime(l.at, ev.siteTimeZone)}>
                    {fmtTime(l.at, ev.siteTimeZone)}
                  </span>
                  <span className={`lwho ${actorCls(l.actor)}`} title={l.actor.type}>
                    {actorName(l.actor)}
                  </span>
                  <span>
                    {l.text}
                    {call && rec && <RecordingView eventId={ev.id} actionId={call.id} rec={rec} />}
                    {l.kind === 'talk_down' && l.refs.ok !== false && typeof l.refs.actionId === 'string' && <AudioButton url={api.talkdownAudioUrl(l.refs.actionId)} label="Play the audio" small />}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- panel ---------- */

export function ContextPanel({ c }: { c: Ctl }) {
  const pendingSteps = c.ev.mission?.steps.filter((s) => s.status === 'pending').length ?? 0;
  const tabs = (Object.keys(TAB_META) as CtxTab[]).filter((k) => k !== 'agent' || c.agent.on);
  const cur: CtxTab = tabs.includes(c.ctx) ? c.ctx : 'mission';
  const agentPaused = c.ev.agentPaused === true || (c.agent.data?.visible === true && c.agent.data.paused);
  return (
    <aside className="pane zc" aria-label="Event context">
      <div className="ctabs" role="tablist" aria-label="Context">
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={cur === k} className={`${cur === k ? 'on' : ''}${k === 'agent' ? ' aitab' : ''}`} onClick={() => c.setCtx(k)} title={k === 'agent' ? 'Agent panel (G)' : undefined}>
            <Icon name={TAB_META[k].icon} />
            <span>
              {TAB_META[k].label}
              {k === 'mission' && pendingSteps > 0 ? <span className="tabn">{pendingSteps}</span> : null}
              {k === 'contacts' && hasLiveCall(c.ev.actions) ? <span className="tabn live">●</span> : null}
              {k === 'agent' && agentPaused ? <span className="tabn" title="Agent paused">II</span> : null}
              {k === 'agent' && c.approvals.length > 0 ? (
                <span className="tabn ask" title="The agent is waiting for your OK (Y / N)">
                  {c.approvals.length}
                </span>
              ) : null}
            </span>
          </button>
        ))}
      </div>
      <div className="cbody" role="tabpanel" aria-label={TAB_META[cur].label}>
        {cur === 'agent' && <AgentTab c={c} />}
        {cur === 'mission' && <Mission c={c} />}
        {cur === 'map' && <MapTab c={c} />}
        {cur === 'contacts' && <Contacts c={c} />}
        {cur === 'intel' && <Intel c={c} />}
        {cur === 'log' && <Log c={c} />}
      </div>
    </aside>
  );
}

export function EmptyContext({ text }: { text: string }) {
  return (
    <aside className="pane zc" aria-label="Event context">
      <div className="cbody">
        <div className="note qempty">{text}</div>
      </div>
    </aside>
  );
}
